import {useMemo} from 'react';

import {DripSequenceRowSparse} from 'shells/row/drip-sequence-row/drip-sequence-row-sparse';
import {PersonalEmailSequenceRowSparse} from 'shells/row/personal-email-sequence-row/personal-email-sequence-row-sparse';

import type {EmailAutomationStatusesFragment, EmailSequenceFragment} from 'nutshell-graphql-types';

import {
    sortSequences,
    getEmailSequenceStartTrigger,
    getEmailSequenceTitle,
    getAutomationProblemDescription,
} from './helpers';
import type {PersonalEmailSequenceToViewSparse, FlattenedDripSequence} from './types';

interface Props {
    tab: string;
    dripSequences?: FlattenedDripSequence[];
    manuallyStartedEmailSequences: EmailSequenceFragment[];
    personalEmailSequences: EmailAutomationStatusesFragment[];
    onSeeDripSequenceDetails: (id: string, contactId: string, contactName: string) => void;
    onSeePersonalEmailSequenceDetails: (
        emailSequenceToView: PersonalEmailSequenceToViewSparse
    ) => void;
    onCancelAutomatedEmailSequence?: (sequenceId: string) => void;
    onStartAutomatedEmailSequence?: (sequenceId: string) => void;
    onCancelManuallyStartedEmailSequence?: (sequenceId: string) => void;
    onCancelDripSequence?: (sequenceId: string) => void;
}

export function EmailSequencesTab(props: Props) {
    const {
        onCancelAutomatedEmailSequence,
        onStartAutomatedEmailSequence,
        onCancelManuallyStartedEmailSequence,
        onCancelDripSequence,
    } = props;

    const sortedSequences: Array<
        FlattenedDripSequence | EmailSequenceFragment | EmailAutomationStatusesFragment
    > = useMemo(() => {
        const allSequences = [
            ...(props.dripSequences ?? []),
            ...props.manuallyStartedEmailSequences,
            ...props.personalEmailSequences,
        ];

        return sortSequences(props.tab, allSequences);
    }, [
        props.tab,
        props.dripSequences,
        props.manuallyStartedEmailSequences,
        props.personalEmailSequences,
    ]);

    const mappedSequences: React.ReactNode[] = sortedSequences.map(
        (
            sequence:
                | FlattenedDripSequence
                | EmailSequenceFragment
                | EmailAutomationStatusesFragment
        ): React.ReactElement | null => {
            if (sequence.__typename === 'DripProgression') {
                return (
                    <DripSequenceRowSparse
                        key={sequence.id}
                        title={sequence.campaign.name}
                        nextEditionSendTime={sequence.nextEditionSendTime}
                        startTrigger={
                            sequence.campaign.startTrigger &&
                            sequence.campaign.startTrigger.humanReadableSubtitle
                        }
                        onSeeDetails={() =>
                            props.onSeeDripSequenceDetails(
                                sequence.id,
                                sequence.contactId,
                                sequence.contactName
                            )
                        }
                        recipientName={sequence.contactName}
                        numEditionsSent={sequence.numEditionsSent}
                        numEditionsTotal={sequence.editions.length}
                        onCancel={
                            onCancelDripSequence
                                ? () => onCancelDripSequence(sequence.id)
                                : undefined
                        }
                    />
                );
            } else if (sequence.__typename === 'EmailSequence') {
                const title = sequence.emailSequenceTemplate && sequence.emailSequenceTemplate.name;
                const recipientNames =
                    sequence.emailsQueued[0] &&
                    sequence.emailsQueued[0].to.map((to) =>
                        to.avatarEntity ? to.avatarEntity.name : null
                    );

                return (
                    <PersonalEmailSequenceRowSparse
                        key={sequence.id}
                        startedFromStageEntered={false}
                        title={title}
                        problemDescription={null}
                        startTrigger={null}
                        emailSequence={sequence}
                        onSeeDetails={() =>
                            props.onSeePersonalEmailSequenceDetails({
                                type: 'manually-started',
                                id: sequence.id,
                            })
                        }
                        finalEmailSentTime={
                            sequence.nextUnsentEmail === null
                                ? // @ts-expect-error
                                  sequence.emailsQueued[sequence.emailsQueued.length - 1]
                                      .deliveryTime
                                : null
                        }
                        recipientNames={recipientNames}
                        onCancel={
                            onCancelManuallyStartedEmailSequence
                                ? () => onCancelManuallyStartedEmailSequence(sequence.id)
                                : undefined
                        }
                    />
                );
            } else if (sequence.__typename === 'EmailAutomationStatus') {
                const startTrigger = getEmailSequenceStartTrigger(sequence);
                const title = getEmailSequenceTitle(sequence);
                const problemDescription = getAutomationProblemDescription(sequence);
                const recipientNames =
                    sequence.emailSequence &&
                    sequence.emailSequence.emailsQueued[0] &&
                    sequence.emailSequence.emailsQueued[0].to.map((to) =>
                        to.avatarEntity ? to.avatarEntity.name : null
                    );

                return (
                    <PersonalEmailSequenceRowSparse
                        key={sequence.id}
                        title={title}
                        startedFromStageEntered={true}
                        emailSequence={sequence.emailSequence}
                        problemDescription={problemDescription}
                        onSeeDetails={() =>
                            props.onSeePersonalEmailSequenceDetails({
                                type: 'stage',
                                id: sequence.id,
                            })
                        }
                        startTrigger={startTrigger}
                        finalEmailSentTime={
                            sequence.unsentMessageCount === 0 &&
                            sequence.emailSequence &&
                            sequence.emailSequence.emailsQueued &&
                            sequence.emailSequence.emailsQueued.length > 0
                                ? // @ts-expect-error
                                  sequence.emailSequence.emailsQueued[
                                      sequence.emailSequence.emailsQueued.length - 1
                                  ].deliveryTime
                                : null
                        }
                        recipientNames={recipientNames}
                        onCancel={
                            onCancelAutomatedEmailSequence
                                ? () => onCancelAutomatedEmailSequence(sequence.id)
                                : undefined
                        }
                        onStart={
                            onStartAutomatedEmailSequence && sequence.canStart
                                ? () => onStartAutomatedEmailSequence(sequence.id)
                                : undefined
                        }
                    />
                );
            }

            return null;
        }
    );

    return mappedSequences;
}
